import { Hidden } from '@orobix/orobix-fe-components'
import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Observation } from '../../types'
import ThumbContextualMenu from '../ThumbContextualMenu/ThumbContextualMenu'
import ThumbTemplate from '../ThumbTemplate/ThumbTemplate'
import useStyles from './ThumbWithMenu.styles'

type MenuItem = {
  label: string
  icon?: React.ReactNode
  type?: 'default' | 'destroy'
  disabled?: boolean
  title?: string
  onClick: () => void
}

type ThumbWithMenuProps = React.ComponentProps<typeof ThumbTemplate> & {
  isActive: boolean
  isDisabled: boolean
  isClickDisabled?: boolean
  contextualItems?: MenuItem[]
  onClick: () => void
  observation: Observation
}

const ThumbWithMenu = ({
  isActive,
  isDisabled,
  isClickDisabled,
  contextualItems,
  onClick,
  ...rest
}: ThumbWithMenuProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [imageOverlay, showImageOverlay] = useState(false)
  const { observation } = rest

  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    if (isDisabled) {
      showImageOverlay(false)
    }
  }, [isDisabled])

  const LATENCY = 230
  const clickRef = useRef(0)

  const handleClick = () => {
    clickRef.current += 1
    const timer = setTimeout(() => {
      if (clickRef.current === 1) {
        onClick()
      } else if (clickRef.current === 2) {
        setShowMenu(true)
      }
      clickRef.current = 0
    }, LATENCY)

    return () => clearTimeout(timer)
  }

  return (
    <div
      className={cx(
        classes.thumb,
        isActive && classes.active,
        isDisabled && classes.disabled
      )}
    >
      <ThumbTemplate
        imageContent={
          imageOverlay && (
            <div className={classes.imageOverlay}>
              {contextualItems && <div className={classes.menuBackground} />}

              <button
                className={classes.expandButton}
                type="button"
                onClick={handleClick}
                style={isClickDisabled ? { cursor: 'default' } : undefined}
              >
                <Hidden>{t('Image details')}</Hidden>
              </button>

              {contextualItems && (
                <div className={classes.contextualMenu}>
                  <ThumbContextualMenu
                    showImageOverlay={showImageOverlay}
                    items={contextualItems}
                    observation={observation}
                    showMenu={showMenu}
                    setShowMenu={setShowMenu}
                  />
                </div>
              )}
            </div>
          )
        }
        onMouseEnterImage={() => {
          if (!isDisabled) showImageOverlay(true)
          setShowMenu(false)
        }}
        onMouseLeaveImage={() => {
          if (!isDisabled) showImageOverlay(false)
          setShowMenu(false)
        }}
        isDisabled={isDisabled}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
    </div>
  )
}

export default ThumbWithMenu
